import {IRange} from "@pg-design/inputs-module";
import {decimalSeparatorThousand} from "@pg-mono/string-utils";
import {Currency, OfferType} from "@pg-rp/common-types";

import {getAreaFilterLabelText} from "../../real_estate/utils/filter_labels";
import {floorOptions, floorOptionsForHouse} from "../../search/components/FloorChoicesSwitch";
import {DropdownToOpen} from "../../search/constants/DropdownToOpen";
import {getRoomLabel} from "../../search/utils/room_field_data_translations";
import {hitGoogleTagManager} from "../../tracking/google_tag_manager/hit_google_tag_manager";
import {SearchSortString} from "../list/constants/SearchSort";

type EventName = "listing_offer_modal_filters" | "invest_filters";
export type FilterCategory = "Pokoje" | "Metraż" | "Piętra" | "Sortuj" | "Budżet";
type FilterType = "budget" | "rooms" | "area" | "floor" | "sort";

export interface HitOfferModalFiltersParams {
    eventName: EventName;
    filterCategory?: FilterCategory | "filters";
    customLabel?: string;
}

export interface TrackFilterValueParams {
    eventName: EventName;
    filterType: FilterType;
    value: IRange<string> | IRange<number | ""> | number[] | SearchSortString | number;
    currency?: Currency;
    offerType?: OfferType;
    sortOptions?: {label: string; value: number; slug?: string}[];
}

const getRoomFilterLabel = (value: IRange<number | "">): string => {
    return getRoomLabel(value, "Pokoje", true) || "Pokoje";
};

const getAreaFilterLabel = (value: IRange<string>): string => {
    const {text} = getAreaFilterLabelText(value, "Metraż");

    if (text) {
        return `${text} m²`;
    }

    return "Metraż";
};

const getFloorFilterLabel = (value: number[], offerType?: OfferType): string => {
    const options = offerType === OfferType.HOUSE ? floorOptionsForHouse : floorOptions;
    const selectedLabels = value.map((floorValue) => options.find((opt) => opt.value === floorValue)?.label).filter(Boolean);

    if (selectedLabels.length === 0) {
        return "Piętra";
    }

    if (selectedLabels.length === 1) {
        return selectedLabels[0] || "Piętra";
    }

    return selectedLabels.join(", ");
};

const getBudgetFilterLabel = (value: IRange<string>, currency: string = "zł"): string => {
    const {lower, upper} = value;

    if (!lower && !upper) {
        return "Budżet";
    }

    if (lower && upper) {
        return `${decimalSeparatorThousand(lower)} - ${decimalSeparatorThousand(upper)} ${currency}`;
    }

    if (lower) {
        return `od ${decimalSeparatorThousand(lower)} ${currency}`;
    }

    if (upper) {
        return `do ${decimalSeparatorThousand(upper)} ${currency}`;
    }

    return "Budżet";
};

const getSortFilterLabel = (sortValue?: SearchSortString | string | null): string => {
    if (!sortValue) {
        return "Sortuj";
    }

    const sortOptions: Record<string, string> = {
        [SearchSortString.DEFAULT]: "Polecane",
        [SearchSortString.LATEST]: "Najnowsze",
        [SearchSortString.PRICE]: "Najtańsze",
        [SearchSortString.PRICE_M2]: "Najtańsze (za m2)",
        [SearchSortString.CONSTRUCTION_DATE]: "Termin oddania"
    };

    return sortOptions[sortValue] || "Sortuj";
};

const getSortFilterLabelFromOptions = (sortValue: number, sortOptions: {label: string; value: number; slug?: string}[]): string => {
    const option = sortOptions.find((opt) => opt.value === sortValue);

    return option?.label || "Sortuj";
};

const getMobileFiltersLabel = (
    filters: {
        rooms?: IRange<number | "">;
        area?: IRange<string>;
        floor_choices?: number[];
        house_storeys?: number[];
        price?: IRange<string>;
        instalment?: number | "";
    },
    offerType?: OfferType,
    currency?: string
): {categories: string; options: string} => {
    const categories: string[] = [];
    const options: string[] = [];
    const floorChoices = filters.floor_choices || filters.house_storeys;

    if (filters.price && (filters.price.lower || filters.price.upper)) {
        categories.push("Budżet");

        const budgetLabel = getBudgetFilterLabel(filters.price, currency);

        if (budgetLabel !== "Budżet") {
            options.push(budgetLabel);
        }
    }

    if (filters.rooms && (filters.rooms.lower || filters.rooms.upper)) {
        categories.push("Pokoje");

        const roomLabel = getRoomFilterLabel(filters.rooms);

        if (roomLabel !== "Pokoje") {
            options.push(roomLabel);
        }
    }

    if (filters.area && (filters.area.lower || filters.area.upper)) {
        categories.push("Metraż");

        const areaLabel = getAreaFilterLabel(filters.area);

        if (areaLabel !== "Metraż") {
            options.push(areaLabel);
        }
    }

    if (floorChoices && floorChoices.length > 0) {
        categories.push("Piętra");

        const floorLabel = getFloorFilterLabel(floorChoices, offerType);

        if (floorLabel !== "Piętra") {
            options.push(floorLabel);
        }
    }

    return {
        categories: categories.join(", "),
        options: options.join(", ")
    };
};

const getFilterCategoryFromDropdown = (dropdownToOpen: DropdownToOpen | null): FilterCategory | undefined => {
    if (dropdownToOpen === null) {
        return undefined;
    }

    switch (dropdownToOpen) {
        case DropdownToOpen.PRICE:
            return "Budżet";
        case DropdownToOpen.ROOMS:
            return "Pokoje";
        case DropdownToOpen.AREA:
            return "Metraż";
        case DropdownToOpen.FLOORS:
            return "Piętra";
        default:
            return undefined;
    }
};

const getFilterCategoryFromType = (filterType: FilterType): FilterCategory => {
    switch (filterType) {
        case "budget":
            return "Budżet";
        case "rooms":
            return "Pokoje";
        case "area":
            return "Metraż";
        case "floor":
            return "Piętra";
        case "sort":
            return "Sortuj";
    }
};

const getFilterLabel = (
    filterType: FilterType,
    value: IRange<string> | IRange<number | ""> | number[] | SearchSortString | number,
    currency?: Currency,
    offerType?: OfferType,
    sortOptions?: {label: string; value: number; slug?: string}[]
): string => {
    switch (filterType) {
        case "budget": {
            const currencyUnit = currency === Currency.EURO ? "eur" : "zł";
            return getBudgetFilterLabel(value as IRange<string>, currencyUnit);
        }
        case "rooms":
            return getRoomFilterLabel(value as IRange<number | "">);
        case "area":
            return getAreaFilterLabel(value as IRange<string>);
        case "floor":
            return getFloorFilterLabel(value as number[], offerType);
        case "sort":
            if (sortOptions && typeof value === "number") {
                return getSortFilterLabelFromOptions(value, sortOptions);
            }
            return getSortFilterLabel(value as SearchSortString);
    }
};

const hasFilterValue = (filterType: FilterType, value: IRange<string> | IRange<number | ""> | number[] | SearchSortString | number): boolean => {
    switch (filterType) {
        case "budget":
        case "rooms":
        case "area": {
            const range = value as IRange<string> | IRange<number | "">;
            return !!(range.lower || range.upper);
        }
        case "floor":
            return Array.isArray(value) && value.length > 0;
        case "sort":
            return typeof value === "number" && value !== 0;
    }
};

export const trackFilterOpen = (dropdownToOpen: DropdownToOpen | null, eventName: EventName) => {
    const filterCategory = getFilterCategoryFromDropdown(dropdownToOpen);

    if (filterCategory) {
        hitOfferModalFiltersOpen({eventName, filterCategory});
    }
};

export const trackFilterApply = (params: TrackFilterValueParams) => {
    const {eventName, filterType, value, currency, offerType, sortOptions} = params;

    if (!hasFilterValue(filterType, value)) {
        return;
    }

    const filterCategory = getFilterCategoryFromType(filterType);
    const label = getFilterLabel(filterType, value, currency, offerType, sortOptions);
    const customLabel = label !== filterCategory ? label : undefined;

    hitOfferModalFiltersApply({eventName, filterCategory, customLabel});
};

export const trackFilterClear = (params: TrackFilterValueParams) => {
    const {eventName, filterType, value, currency, offerType, sortOptions} = params;

    if (!hasFilterValue(filterType, value)) {
        return;
    }

    const filterCategory = getFilterCategoryFromType(filterType);
    const label = getFilterLabel(filterType, value, currency, offerType, sortOptions);
    const customLabel = label !== filterCategory ? label : undefined;

    hitOfferModalFiltersClear({eventName, filterCategory, customLabel});
};

export const trackFilterRemove = (params: TrackFilterValueParams) => {
    const {eventName, filterType, value, currency, offerType, sortOptions} = params;

    if (!hasFilterValue(filterType, value)) {
        return;
    }

    const filterCategory = getFilterCategoryFromType(filterType);
    const label = getFilterLabel(filterType, value, currency, offerType, sortOptions);
    const customLabel = label !== filterCategory ? label : undefined;

    hitOfferModalFiltersRemove({eventName, filterCategory, customLabel});
};

export const trackFilterOpenForCategory = ({filterCategory, eventName}: {filterCategory: FilterCategory | "filters"; eventName: EventName}) => {
    hitOfferModalFiltersOpen({eventName, filterCategory});
};

export const trackFilterApplyForCategories = ({
    eventName,
    filterCategory,
    customLabel
}: {
    eventName: EventName;
    filterCategory: FilterCategory | "filters";
    customLabel?: string;
}) => {
    hitOfferModalFiltersApply({eventName, filterCategory, customLabel});
};

export const trackFilterClearForCategories = ({
    eventName,
    filterCategory,
    customLabel
}: {
    eventName: EventName;
    filterCategory: FilterCategory | "filters";
    customLabel?: string;
}) => {
    hitOfferModalFiltersClear({eventName, filterCategory, customLabel});
};

const hitOfferModalFiltersOpen = ({eventName, filterCategory}: HitOfferModalFiltersParams) => {
    hitGoogleTagManager({
        event: eventName,
        event_action: "open",
        event_label: filterCategory
    });
};

const hitOfferModalFiltersApply = ({eventName, filterCategory = "filters", customLabel}: HitOfferModalFiltersParams) => {
    const gtmData = {
        event: eventName,
        event_action: "apply",
        event_label: filterCategory,
        ...(customLabel ? {custom_label: customLabel} : {})
    };

    hitGoogleTagManager(gtmData);
};

const hitOfferModalFiltersClear = ({eventName, filterCategory = "filters", customLabel}: HitOfferModalFiltersParams) => {
    const gtmData = {
        event: eventName,
        event_action: "clear",
        event_label: filterCategory,
        ...(customLabel ? {custom_label: customLabel} : {})
    };

    hitGoogleTagManager(gtmData);
};

const hitOfferModalFiltersRemove = ({eventName, filterCategory = "filters", customLabel}: HitOfferModalFiltersParams) => {
    const gtmData = {
        event: eventName,
        event_action: "remove",
        event_label: filterCategory,
        ...(customLabel ? {custom_label: customLabel} : {})
    };

    hitGoogleTagManager(gtmData);
};

export const getMobileFiltersLabelForTracking = getMobileFiltersLabel;
import {IRange} from "@pg-design/inputs-module";
import {decimalSeparatorThousand} from "@pg-mono/string-utils";
import {Currency, OfferType} from "@pg-rp/common-types";

import {getAreaFilterLabelText} from "../../real_estate/utils/filter_labels";
import {floorOptions, floorOptionsForHouse} from "../../search/components/FloorChoicesSwitch";
import {DropdownToOpen} from "../../search/constants/DropdownToOpen";
import {getRoomLabel} from "../../search/utils/room_field_data_translations";
import {hitGoogleTagManager} from "../../tracking/google_tag_manager/hit_google_tag_manager";
import {SearchSortString} from "../list/constants/SearchSort";

type EventName = "listing_offer_modal_filters" | "invest_filters";
export type FilterCategory = "Pokoje" | "Metraż" | "Piętra" | "Sortuj" | "Budżet";
type FilterType = "budget" | "rooms" | "area" | "floor" | "sort";

export interface HitOfferModalFiltersParams {
    eventName: EventName;
    filterCategory?: FilterCategory | "filters";
    customLabel?: string;
}

export interface TrackFilterValueParams {
    eventName: EventName;
    filterType: FilterType;
    value: IRange<string> | IRange<number | ""> | number[] | SearchSortString | number;
    currency?: Currency;
    offerType?: OfferType;
    sortOptions?: {label: string; value: number; slug?: string}[];
}

const getRoomFilterLabel = (value: IRange<number | "">): string => {
    return getRoomLabel(value, "Pokoje", true) || "Pokoje";
};

const getAreaFilterLabel = (value: IRange<string>): string => {
    const {text} = getAreaFilterLabelText(value, "Metraż");

    if (text) {
        return `${text} m²`;
    }

    return "Metraż";
};

const getFloorFilterLabel = (value: number[], offerType?: OfferType): string => {
    const options = offerType === OfferType.HOUSE ? floorOptionsForHouse : floorOptions;
    const selectedLabels = value.map((floorValue) => options.find((opt) => opt.value === floorValue)?.label).filter(Boolean);

    if (selectedLabels.length === 0) {
        return "Piętra";
    }

    if (selectedLabels.length === 1) {
        return selectedLabels[0] || "Piętra";
    }

    return selectedLabels.join(", ");
};

const getBudgetFilterLabel = (value: IRange<string>, currency: string = "zł"): string => {
    const {lower, upper} = value;

    if (!lower && !upper) {
        return "Budżet";
    }

    if (lower && upper) {
        return `${decimalSeparatorThousand(lower)} - ${decimalSeparatorThousand(upper)} ${currency}`;
    }

    if (lower) {
        return `od ${decimalSeparatorThousand(lower)} ${currency}`;
    }

    if (upper) {
        return `do ${decimalSeparatorThousand(upper)} ${currency}`;
    }

    return "Budżet";
};

const getSortFilterLabel = (sortValue?: SearchSortString | string | null): string => {
    if (!sortValue) {
        return "Sortuj";
    }

    const sortOptions: Record<string, string> = {
        [SearchSortString.DEFAULT]: "Polecane",
        [SearchSortString.LATEST]: "Najnowsze",
        [SearchSortString.PRICE]: "Najtańsze",
        [SearchSortString.PRICE_M2]: "Najtańsze (za m2)",
        [SearchSortString.CONSTRUCTION_DATE]: "Termin oddania"
    };

    return sortOptions[sortValue] || "Sortuj";
};

const getSortFilterLabelFromOptions = (sortValue: number, sortOptions: {label: string; value: number; slug?: string}[]): string => {
    const option = sortOptions.find((opt) => opt.value === sortValue);

    return option?.label || "Sortuj";
};

const getMobileFiltersLabel = (
    filters: {
        rooms?: IRange<number | "">;
        area?: IRange<string>;
        floor_choices?: number[];
        house_storeys?: number[];
        price?: IRange<string>;
        instalment?: number | "";
    },
    offerType?: OfferType,
    currency?: string
): {categories: string; options: string} => {
    const categories: string[] = [];
    const options: string[] = [];
    const floorChoices = filters.floor_choices || filters.house_storeys;

    if (filters.price && (filters.price.lower || filters.price.upper)) {
        categories.push("Budżet");

        const budgetLabel = getBudgetFilterLabel(filters.price, currency);

        if (budgetLabel !== "Budżet") {
            options.push(budgetLabel);
        }
    }

    if (filters.rooms && (filters.rooms.lower || filters.rooms.upper)) {
        categories.push("Pokoje");

        const roomLabel = getRoomFilterLabel(filters.rooms);

        if (roomLabel !== "Pokoje") {
            options.push(roomLabel);
        }
    }

    if (filters.area && (filters.area.lower || filters.area.upper)) {
        categories.push("Metraż");

        const areaLabel = getAreaFilterLabel(filters.area);

        if (areaLabel !== "Metraż") {
            options.push(areaLabel);
        }
    }

    if (floorChoices && floorChoices.length > 0) {
        categories.push("Piętra");

        const floorLabel = getFloorFilterLabel(floorChoices, offerType);

        if (floorLabel !== "Piętra") {
            options.push(floorLabel);
        }
    }

    return {
        categories: categories.join(", "),
        options: options.join(", ")
    };
};

const getFilterCategoryFromDropdown = (dropdownToOpen: DropdownToOpen | null): FilterCategory | undefined => {
    if (dropdownToOpen === null) {
        return undefined;
    }

    switch (dropdownToOpen) {
        case DropdownToOpen.PRICE:
            return "Budżet";
        case DropdownToOpen.ROOMS:
            return "Pokoje";
        case DropdownToOpen.AREA:
            return "Metraż";
        case DropdownToOpen.FLOORS:
            return "Piętra";
        default:
            return undefined;
    }
};

const getFilterCategoryFromType = (filterType: FilterType): FilterCategory => {
    switch (filterType) {
        case "budget":
            return "Budżet";
        case "rooms":
            return "Pokoje";
        case "area":
            return "Metraż";
        case "floor":
            return "Piętra";
        case "sort":
            return "Sortuj";
    }
};

const getFilterLabel = (
    filterType: FilterType,
    value: IRange<string> | IRange<number | ""> | number[] | SearchSortString | number,
    currency?: Currency,
    offerType?: OfferType,
    sortOptions?: {label: string; value: number; slug?: string}[]
): string => {
    switch (filterType) {
        case "budget": {
            const currencyUnit = currency === Currency.EURO ? "eur" : "zł";
            return getBudgetFilterLabel(value as IRange<string>, currencyUnit);
        }
        case "rooms":
            return getRoomFilterLabel(value as IRange<number | "">);
        case "area":
            return getAreaFilterLabel(value as IRange<string>);
        case "floor":
            return getFloorFilterLabel(value as number[], offerType);
        case "sort":
            if (sortOptions && typeof value === "number") {
                return getSortFilterLabelFromOptions(value, sortOptions);
            }
            return getSortFilterLabel(value as SearchSortString);
    }
};

const hasFilterValue = (filterType: FilterType, value: IRange<string> | IRange<number | ""> | number[] | SearchSortString | number): boolean => {
    switch (filterType) {
        case "budget":
        case "rooms":
        case "area": {
            const range = value as IRange<string> | IRange<number | "">;
            return !!(range.lower || range.upper);
        }
        case "floor":
            return Array.isArray(value) && value.length > 0;
        case "sort":
            return typeof value === "number" && value !== 0;
    }
};

export const trackFilterOpen = (dropdownToOpen: DropdownToOpen | null, eventName: EventName) => {
    const filterCategory = getFilterCategoryFromDropdown(dropdownToOpen);

    if (filterCategory) {
        hitOfferModalFiltersOpen({eventName, filterCategory});
    }
};

export const trackFilterApply = (params: TrackFilterValueParams) => {
    const {eventName, filterType, value, currency, offerType, sortOptions} = params;

    if (!hasFilterValue(filterType, value)) {
        return;
    }

    const filterCategory = getFilterCategoryFromType(filterType);
    const label = getFilterLabel(filterType, value, currency, offerType, sortOptions);
    const customLabel = label !== filterCategory ? label : undefined;

    hitOfferModalFiltersApply({eventName, filterCategory, customLabel});
};

export const trackFilterClear = (params: TrackFilterValueParams) => {
    const {eventName, filterType, value, currency, offerType, sortOptions} = params;

    if (!hasFilterValue(filterType, value)) {
        return;
    }

    const filterCategory = getFilterCategoryFromType(filterType);
    const label = getFilterLabel(filterType, value, currency, offerType, sortOptions);
    const customLabel = label !== filterCategory ? label : undefined;

    hitOfferModalFiltersClear({eventName, filterCategory, customLabel});
};

export const trackFilterRemove = (params: TrackFilterValueParams) => {
    const {eventName, filterType, value, currency, offerType, sortOptions} = params;

    if (!hasFilterValue(filterType, value)) {
        return;
    }

    const filterCategory = getFilterCategoryFromType(filterType);
    const label = getFilterLabel(filterType, value, currency, offerType, sortOptions);
    const customLabel = label !== filterCategory ? label : undefined;

    hitOfferModalFiltersRemove({eventName, filterCategory, customLabel});
};

export const trackFilterOpenForCategory = ({filterCategory, eventName}: {filterCategory: FilterCategory | "filters"; eventName: EventName}) => {
    hitOfferModalFiltersOpen({eventName, filterCategory});
};

export const trackFilterApplyForCategories = ({
    eventName,
    filterCategory,
    customLabel
}: {
    eventName: EventName;
    filterCategory: FilterCategory | "filters";
    customLabel?: string;
}) => {
    hitOfferModalFiltersApply({eventName, filterCategory, customLabel});
};

export const trackFilterClearForCategories = ({
    eventName,
    filterCategory,
    customLabel
}: {
    eventName: EventName;
    filterCategory: FilterCategory | "filters";
    customLabel?: string;
}) => {
    hitOfferModalFiltersClear({eventName, filterCategory, customLabel});
};

const hitOfferModalFiltersOpen = ({eventName, filterCategory}: HitOfferModalFiltersParams) => {
    hitGoogleTagManager({
        event: eventName,
        event_action: "open",
        event_label: filterCategory
    });
};

const hitOfferModalFiltersApply = ({eventName, filterCategory = "filters", customLabel}: HitOfferModalFiltersParams) => {
    const gtmData = {
        event: eventName,
        event_action: "apply",
        event_label: filterCategory,
        ...(customLabel ? {custom_label: customLabel} : {})
    };

    hitGoogleTagManager(gtmData);
};

const hitOfferModalFiltersClear = ({eventName, filterCategory = "filters", customLabel}: HitOfferModalFiltersParams) => {
    const gtmData = {
        event: eventName,
        event_action: "clear",
        event_label: filterCategory,
        ...(customLabel ? {custom_label: customLabel} : {})
    };

    hitGoogleTagManager(gtmData);
};

const hitOfferModalFiltersRemove = ({eventName, filterCategory = "filters", customLabel}: HitOfferModalFiltersParams) => {
    const gtmData = {
        event: eventName,
        event_action: "remove",
        event_label: filterCategory,
        ...(customLabel ? {custom_label: customLabel} : {})
    };

    hitGoogleTagManager(gtmData);
};

export const getMobileFiltersLabelForTracking = getMobileFiltersLabel;
